import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Save } from "lucide-react";
import { api } from "@/lib/api";
import { Seo } from "@/components/Seo";

const TABS = [["courses", "Courses & Certification"], ["assessments", "Assessments"], ["rewards", "Performance Rewards"]];

export default function AdminTraining({ tab = "courses" }) {
  const [active, setActive] = useState(tab);
  const [services, setServices] = useState([]);
  const [courses, setCourses] = useState([]);
  const [rewards, setRewards] = useState("");
  const [edits, setEdits] = useState({});
  const [qEdit, setQEdit] = useState(null); // {sid, name, json}

  const load = () => {
    api.get("/admin/applicator-services").then(({ data }) => setServices(data)).catch(() => {});
    api.get("/admin/training/courses").then(({ data }) => setCourses(data)).catch(() => {});
    api.get("/admin/performance-rewards").then(({ data }) => setRewards(JSON.stringify(data.levels || [], null, 2))).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const setField = (sid, k, v) => setEdits((e) => ({ ...e, [sid]: { ...(e[sid] || {}), [k]: v } }));
  const saveCert = async (s) => {
    const e = edits[s.id] || {};
    const reminders = (e.reminder_days ?? (s.reminder_days || [60, 30, 7]).join(", "));
    await api.put(`/admin/applicator-services/${s.id}/cert-config`, {
      cert_mode: e.cert_mode ?? s.cert_mode ?? "ADMIN",
      cert_validity_days: Number(e.cert_validity_days ?? s.cert_validity_days ?? 365),
      pass_percentage: Number(e.pass_percentage ?? s.pass_percentage ?? 70),
      max_attempts: Number(e.max_attempts ?? s.max_attempts ?? 3),
      retake_wait_hours: Number(e.retake_wait_hours ?? s.retake_wait_hours ?? 0),
      reminder_days: String(reminders).split(",").map((x) => Number(x.trim())).filter((x) => !isNaN(x)),
      recertify_on_version_change: e.recertify_on_version_change ?? s.recertify_on_version_change ?? false,
      course_version: Number(e.course_version ?? s.course_version ?? 1),
    });
    toast.success("Saved"); load();
  };
  const saveQuestions = async () => {
    try {
      const questions = JSON.parse(qEdit.json);
      await api.put(`/admin/training/courses/${qEdit.sid}`, { questions, question_count: questions.length });
      toast.success("Questions saved"); setQEdit(null); load();
    } catch { toast.error("Invalid JSON"); }
  };
  const saveRewards = async () => {
    try { await api.put("/admin/performance-rewards", { levels: JSON.parse(rewards) }); toast.success("Rewards saved"); }
    catch { toast.error("Invalid JSON"); }
  };

  const courseBySvc = courses.reduce((a, c) => { a[c.service_id] = c; return a; }, {});

  return (
    <div className="min-h-screen bg-[#080808]" data-testid="admin-training-page">
      <Seo title="Admin · Training & Certification" description="Manage applicator training, assessments and rewards" />
      <div className="glass"><div className="max-w-[1100px] mx-auto px-5 h-[60px] flex items-center gap-4">
        <Link to="/admin/applicators" className="flex items-center gap-2 text-sm text-gray-300"><ArrowLeft size={16} /> Applicators</Link>
      </div></div>

      <div className="max-w-[1100px] mx-auto px-5 py-10">
        <h1 className="serif-head text-3xl lg:text-4xl mb-6">Training & Certification</h1>
        <div className="flex gap-2 mb-8 border-b border-white/10">
          {TABS.map(([k, l]) => <button key={k} onClick={() => setActive(k)} data-testid={`admin-training-tab-${k}`} className={`px-4 py-2.5 text-sm ${active === k ? "text-[#C9A45C] border-b-2 border-[#C9A45C]" : "text-gray-400"}`}>{l}</button>)}
        </div>

        {active === "courses" && (
          <div className="space-y-3" data-testid="admin-courses-list">
            {services.filter((s) => s.active).map((s) => {
              const e = edits[s.id] || {};
              const course = courseBySvc[s.id];
              const val = (k, d) => e[k] ?? s[k] ?? d;
              return (
                <div key={s.id} className="hair-border bg-white/[0.02] p-4" data-testid={`admin-course-${s.id}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div><p className="text-sm">{s.name}</p><p className="text-xs text-gray-500">{s.category} · {course ? `${course.module_count || (course.modules||[]).length} modules · ${(course.questions||[]).length} questions` : "no course"}</p></div>
                    <button onClick={() => saveCert(s)} data-testid={`save-cert-${s.id}`} className="inline-flex items-center gap-1.5 border border-white/20 px-3 py-1.5 text-xs hover:bg-white hover:text-black transition-colors"><Save size={12} /> Save</button>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    <div><label className="errah-label block mb-1">Cert Mode</label>
                      <select value={val("cert_mode", "ADMIN")} onChange={(ev) => setField(s.id, "cert_mode", ev.target.value)} data-testid={`cert-mode-${s.id}`} className="w-full bg-[#111] border border-white/15 px-2 py-2 text-xs">
                        <option value="ADMIN">Admin Approval</option><option value="AUTO">Auto Certify</option></select></div>
                    <div><label className="errah-label block mb-1">Validity (days)</label><input type="number" value={val("cert_validity_days", 365)} onChange={(ev) => setField(s.id, "cert_validity_days", ev.target.value)} className="w-full bg-transparent border border-white/15 px-2 py-2 text-xs" /></div>
                    <div><label className="errah-label block mb-1">Pass %</label><input type="number" value={val("pass_percentage", 70)} onChange={(ev) => setField(s.id, "pass_percentage", ev.target.value)} className="w-full bg-transparent border border-white/15 px-2 py-2 text-xs" /></div>
                    <div><label className="errah-label block mb-1">Max Attempts</label><input type="number" value={val("max_attempts", 3)} onChange={(ev) => setField(s.id, "max_attempts", ev.target.value)} className="w-full bg-transparent border border-white/15 px-2 py-2 text-xs" /></div>
                    <div><label className="errah-label block mb-1">Retake Wait (h)</label><input type="number" value={val("retake_wait_hours", 0)} onChange={(ev) => setField(s.id, "retake_wait_hours", ev.target.value)} className="w-full bg-transparent border border-white/15 px-2 py-2 text-xs" /></div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-3">
                    <div><label className="errah-label block mb-1">Reminder Days</label><input value={e.reminder_days ?? (s.reminder_days || [60, 30, 7]).join(", ")} onChange={(ev) => setField(s.id, "reminder_days", ev.target.value)} data-testid={`reminder-days-${s.id}`} placeholder="60, 30, 7" className="w-full bg-transparent border border-white/15 px-2 py-2 text-xs" /></div>
                    <div><label className="errah-label block mb-1">Course Version</label><input type="number" value={val("course_version", 1)} onChange={(ev) => setField(s.id, "course_version", ev.target.value)} data-testid={`course-version-${s.id}`} className="w-full bg-transparent border border-white/15 px-2 py-2 text-xs" /></div>
                    <div><label className="errah-label block mb-1">Recertify on Version Change</label>
                      <select value={String(val("recertify_on_version_change", false))} onChange={(ev) => setField(s.id, "recertify_on_version_change", ev.target.value === "true")} data-testid={`recertify-${s.id}`} className="w-full bg-[#111] border border-white/15 px-2 py-2 text-xs">
                        <option value="false">No</option><option value="true">Yes</option></select></div>
                  </div>
                  {course && <button onClick={() => setQEdit({ sid: s.id, name: s.name, json: JSON.stringify(course.questions || [], null, 2) })} data-testid={`edit-questions-${s.id}`} className="mt-3 text-xs text-[#C9A45C] hover:underline">Edit assessment questions →</button>}
                </div>
              );
            })}
          </div>
        )}

        {active === "assessments" && (
          <div className="space-y-3" data-testid="admin-assessments-list">
            {courses.map((c) => (
              <div key={c.service_id} className="hair-border bg-white/[0.02] p-4 flex items-center justify-between" data-testid={`admin-assessment-${c.service_id}`}>
                <div><p className="text-sm">{c.service_name}</p><p className="text-xs text-gray-500">{c.category} · {(c.questions || []).length} questions · pass {c.pass_percentage}% · {c.max_attempts} attempts</p></div>
                <button onClick={() => setQEdit({ sid: c.service_id, name: c.service_name, json: JSON.stringify(c.questions || [], null, 2) })} className="text-xs border border-white/20 px-3 py-1.5 hover:bg-white hover:text-black transition-colors">Edit Questions</button>
              </div>
            ))}
          </div>
        )}

        {active === "rewards" && (
          <div data-testid="admin-rewards">
            <p className="text-xs text-gray-500 mb-2">Configure performance-reward levels (JSON). Criteria: min_completed_jobs, min_revenue, min_rating, min_qc_pass_rate. Never based on recruitment alone.</p>
            <textarea value={rewards} onChange={(e) => setRewards(e.target.value)} data-testid="rewards-json" rows={16} className="w-full bg-white/[0.03] border border-white/15 p-3 text-xs font-mono focus:border-[#C9A45C] outline-none" />
            <button onClick={saveRewards} data-testid="save-rewards-btn" className="mt-3 inline-flex items-center gap-1.5 bg-[#C9A45C] text-black px-5 py-2.5 text-sm font-semibold hover:bg-[#E4C987] transition-colors"><Save size={15} /> Save Rewards</button>
          </div>
        )}
      </div>

      {qEdit && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setQEdit(null)}>
          <div className="bg-[#0C0C0C] border border-[#C9A45C]/30 w-full max-w-2xl p-6" onClick={(e) => e.stopPropagation()} data-testid="questions-editor">
            <p className="font-display text-lg mb-1">{qEdit.name} — Questions</p>
            <p className="text-xs text-gray-500 mb-3">Each: {"{ id, q, options[], answer (0-based index) }"}</p>
            <textarea value={qEdit.json} onChange={(e) => setQEdit((q) => ({ ...q, json: e.target.value }))} rows={16} className="w-full bg-white/[0.03] border border-white/15 p-3 text-xs font-mono focus:border-[#C9A45C] outline-none" />
            <div className="flex gap-2 mt-3 justify-end">
              <button onClick={() => setQEdit(null)} className="text-xs border border-white/20 px-4 py-2">Cancel</button>
              <button onClick={saveQuestions} data-testid="save-questions-btn" className="text-xs bg-[#C9A45C] text-black px-4 py-2 font-semibold">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
